import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from 'primereact/button';
import { askChatbot } from "../../services/chatbotService";
import ReactMarkdown from 'react-markdown';
import './chatbot.css';

const Chatbot = ({ isNewChat, setIsNewChat }) => {
   const sessionId = useRef(Math.random().toString(36).substring(2));
   const messagesEndRef = useRef(null);
   
   const [query, setQuery] = useState("");
   const [messages, setMessages] = useState(() => {
     const savedMessages = localStorage.getItem('chatMessages');
     return savedMessages ? JSON.parse(savedMessages) : [];
   });
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);

   const predefinedPrompts = [
    "I am a foreigner that wants to open a business in Albania. Where do I start?",
    "What are the tax requirements for a foreign business in Albania?",
    "How do I register a company in Albania?",
    "What are the costs of starting a business in Albania?"
  ];

   const scrollToBottom = useCallback(() => {
     messagesEndRef.current?.scrollTo({
       top: messagesEndRef.current.scrollHeight,
       behavior: "smooth"
     });
   }, []);

   const handlePromptClick = (prompt) => {
    setQuery(prompt);
    handleSubmit({ preventDefault: () => {} }, prompt);
  };

   const showWelcomeMessage = useCallback(async (mounted) => {
     if (!mounted) return;
     const fullMessage = "👋 Welcome! I am your professional AI assistant in business in Albania. How can I help you today?";
     const welcomeMessage = { sender: "bot", text: "" };
     
     setMessages([welcomeMessage]);
     
     for (let i = 0; i < fullMessage.length; i++) {
       if (!mounted) break;
       await new Promise(resolve => setTimeout(resolve, 30));
       welcomeMessage.text = fullMessage.slice(0, i + 1);
       setMessages([{ ...welcomeMessage }]);
     }
   }, []);

   const handleSubmit = useCallback(async (e) => {
     e.preventDefault();
     if (query.trim() === "" || loading) return;

     setLoading(true);
     setError(null);

     const updatedMessages = [...messages, { sender: "user", text: query }];
     setMessages(updatedMessages);

     try {
       const data = await askChatbot(query, sessionId.current);
       setMessages(prev => [...prev, { sender: "bot", text: data.response }]);
     } catch (err) {
       setError("Failed to fetch response.");
       console.error("Error:", err);
     } finally {
       setLoading(false);
       setQuery("");
     }
   }, [query, loading, messages]);

   // Save messages to localStorage
   useEffect(() => {
     localStorage.setItem('chatMessages', JSON.stringify(messages));
   }, [messages]);

   // Handle new chat initialization
   useEffect(() => {
     if (isNewChat) {
       let mounted = true;
       showWelcomeMessage(mounted);
       setIsNewChat(false);
       return () => {
         mounted = false;
       };
     }
   }, [isNewChat, setIsNewChat, showWelcomeMessage]);

   // Handle scroll to bottom on new messages
   useEffect(() => {
     scrollToBottom();
   }, [messages, scrollToBottom]);

   const handleKeyDown = (e) => {
     if (e.key === 'Enter' && !e.shiftKey) {
       e.preventDefault();
       if (query.trim() !== '' && !loading) {
         handleSubmit(e);
       }
     }
   };

   const handleInput = (e) => {
     e.target.style.height = "auto";
     e.target.style.height = `${e.target.scrollHeight}px`;
   };

   return (
     <div className="chatbot-container">
       <div className="chatbot-messages" ref={messagesEndRef}>
         {messages.map((msg, index) => (
           <div key={index} className={`chatbot-message ${msg.sender}`}>
             {msg.sender === "bot" && (
               <img 
                 src={`${process.env.PUBLIC_URL}/Images/chatboticon.png`} 
                 alt="Bot" 
                 className="bot-avatar"
               />
             )}
              {msg.sender === "user" && (
               <img 
                 src={`${process.env.PUBLIC_URL}/Images/user.png`} 
                 alt="User" 
                 className="user-avatar"
               />
             )}
             <div className="message-content">
               {msg.sender === "bot" ? (
                 <ReactMarkdown>{msg.text}</ReactMarkdown>
               ) : (
                 msg.text
               )}
             </div>
           </div>
         ))}
         {loading && (
           <div className="chatbot-message bot">
             <img 
               src={`${process.env.PUBLIC_URL}/Images/chatboticon.png`} 
               alt="Bot" 
               className="bot-avatar"
             />
             <div className="message-content">Working...</div>
           </div>
         )}
         <div />
       </div>
       <div className="chatbot-prompts">
         {predefinedPrompts.map((prompt, index) => (
           <button key={index} className="prompt-button" onClick={() => handlePromptClick(prompt)}>
             {prompt}
           </button>
         ))}
       </div>
       <form className="chatbot-input-area" onSubmit={handleSubmit}>
         <textarea
           type="text"
           placeholder="Ask me a question here ..."
           value={query}
           onChange={(e) => setQuery(e.target.value)}
           onKeyDown={handleKeyDown}
           onInput={handleInput}
           required
         />
         <Button 
           icon="pi pi-search" 
           disabled={loading} 
           type="submit" 
           rounded 
           text 
           raised 
           severity="success" 
           aria-label="Search" 
         />
       </form>
       {error && <div className="chatbot-error">{error}</div>}
     </div>
   );
};

export default Chatbot;
